/**
 * Stripe-Identity-style consent gate for the `awaiting_consent` step.
 * Mounted by FlowRunner when `CONSENT_GIVEN` is available. Agree posts that
 * event; Decline stays local (no engine event — the chart only exposes agree).
 *
 * Partner name is the merchant the applicant is verifying for, and it comes
 * from exactly one place: `snapshot.branding`, which the backend resolves from
 * the case's owning tenant. See {@link partnerNameFromSnapshot} for why the
 * two obvious alternatives are both wrong.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '../ui/BrandMark';
import { apiUrl } from './client';
import { geolocationReason } from './attemptReason';
import {
  isRetryable,
  requestPosition,
  type PositionDenial,
  type PositionFix,
  type PositionOutcome,
} from './geolocation';
import { LocationBlocked } from './LocationBlocked';
import { reportAttempt } from './reportAttempt';
import type { CollectionFlowSnapshot, TenantBranding } from './types';

const CONSENT_EVENT = 'CONSENT_GIVEN';

const PRIVACY_URL =
  import.meta.env.FRONTEND_TRUSTSY_PRIVACY_URL?.trim() ||
  'https://trustsy.com/privacy';

interface ConsentStepProps {
  /**
   * Called with CONSENT_GIVEN when the applicant agrees AND the browser gave up
   * a position. Never called without one: the server refuses the event anyway,
   * and firing it to be refused would turn a permission wall into a generic
   * "something went wrong" screen.
   */
  onAgree: (position: PositionFix) => void;
  /** True while the agree event is in flight. */
  busy?: boolean;
  /**
   * Merchant / platform name shown in “{partner} works with trustsy…”.
   * Supply from {@link partnerNameFromSnapshot}; omitted or null renders a
   * translated generic label rather than a guessed party.
   */
  partnerName?: string | null;
  /**
   * Server-resolved tenant branding — logo and the partner's own policy links.
   *
   * Every field is optional and every one degrades silently: no logo falls back
   * to the generic mark, no policy URL simply omits that link. A consent screen
   * must render correctly for a tenant who has configured nothing.
   */
  branding?: TenantBranding | null;
  /**
   * The applicant's bearer, used ONLY to report a refused position as a
   * failed step attempt. Optional so the component still renders in tests and
   * stories that have no session; absent simply means the refusal is not
   * reported, never that the screen breaks.
   */
  token?: string;
  /**
   * Where the position comes from: the browser, unless a caller supplies one.
   * The console's flow preview does — it runs in a frame no position may be
   * asked from, and an applicant's act there is never sent anywhere.
   */
  locate?: () => Promise<PositionOutcome>;
}

/**
 * The name of the party asking for this applicant's identity.
 *
 * Server-resolved, and deliberately the ONLY source. The two things it
 * replaced were each unsound in a different way:
 *
 * - **`context.partnerName`** — `context` is caller-writable, so any API client
 *   could have rendered "Bangkok Bank needs to verify your identity" on a
 *   screen that then collects a government ID and a selfie. Consent branding
 *   cannot come from the party being consented to.
 * - **`FRONTEND_PARTNER_NAME`** — a build-time constant in a SPA that serves every
 *   tenant from one deployment, so it would have labelled every tenant's flow
 *   with whichever name happened to be baked in.
 *
 * Null means the tenant set no display name. Callers render generic copy in
 * that case; naming the wrong party is worse than naming none.
 */
export function partnerNameFromSnapshot(
  snapshot: Pick<CollectionFlowSnapshot, 'branding'> | null | undefined,
): string | null {
  const name = snapshot?.branding?.display_name;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

export function ConsentStep({
  onAgree,
  busy = false,
  partnerName,
  branding,
  token,
  locate = requestPosition,
}: ConsentStepProps) {
  const { t } = useTranslation();
  const [declined, setDeclined] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  /** True while the browser is resolving a position — can take many seconds. */
  const [locating, setLocating] = useState(false);
  /** Set when the browser would not give one up. Blocks the flow. */
  const [denial, setDenial] = useState<PositionDenial | null>(null);

  const agree = useCallback(async () => {
    setLocating(true);
    setDenial(null);
    const outcome = await locate();
    setLocating(false);
    if (outcome.ok) {
      onAgree(outcome.fix);
      return;
    }
    setDenial(outcome.reason);
    // The ONLY server-side trace of this. The SPA never sends the consent
    // event without a position, so there is no 400 in the request log either
    // — an operator looking at a case that simply stopped here would have
    // nothing at all to go on. Fire-and-forget: a failed report must not add
    // a second failure on top of the first.
    if (token) {
      reportAttempt(token, {
        stepKey: 'awaiting_consent',
        stepKind: 'geolocation',
        reason: geolocationReason(outcome.reason),
        origin: 'client',
        // Keyed by reason, not by a counter: a permanently denied permission
        // is ONE fact however many times the applicant clicks, and the
        // server dedups on (case, attempt_key).
        attemptKey: `geolocation:consent:${outcome.reason}`,
      });
    }
  }, [locate, onAgree, token]);
  const partner =
    (partnerName && partnerName.trim()) ||
    t('flow.consent.partnerFallback', { defaultValue: 'This service' });

  // A logo that fails to load must not leave an empty hole where the party
  // asking for a government ID should be — fall back to the generic mark.
  const logoPath = branding?.logo_url?.trim();
  const logoSrc = logoPath && !logoBroken ? apiUrl(logoPath) : null;

  if (declined) {
    return (
      <div className="consent" data-testid="consent-step">
        <h1 className="consent-title">
          {t('flow.consent.declinedTitle', { defaultValue: 'Verification declined' })}
        </h1>
        <p className="step-text">
          {t('flow.consent.declined', {
            defaultValue: 'You declined verification. You can close this page or go back to agree.',
          })}
        </p>
        <div className="actions">
          <button type="button" className="btn btn--ghost" data-track="back" onClick={() => setDeclined(false)}>
            {t('flow.consent.back', { defaultValue: 'Back' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="consent" data-testid="consent-step">
      <div className="consent-logos" aria-hidden="true">
        <span
          className={
            logoSrc
              ? 'consent-logo consent-logo--partner consent-logo--image'
              : 'consent-logo consent-logo--partner'
          }
          title={partner}
        >
          {logoSrc ? (
            <img
              src={logoSrc}
              alt=""
              className="consent-logo-img consent-logo-img--partner"
              decoding="async"
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <PartnerMark />
          )}
        </span>
        <span className="consent-logo consent-logo--trustsy" title="trustsy">
          <BrandMark size={28} className="consent-logo-img" />
        </span>
      </div>

      <h1 className="consent-title">
        {t('flow.consent.heading', {
          partner,
          defaultValue: '{{partner}} works with trustsy to verify your identity',
        })}
      </h1>

      <ul className="consent-points">
        <li>
          <span className="consent-icon" aria-hidden="true">
            <IconCamera />
          </span>
          <span>
            {t('flow.consent.pointPhoto', {
              defaultValue: "You'll scan a valid photo ID, then take a selfie to make sure it's yours.",
            })}
          </span>
        </li>
        <li>
          <span className="consent-icon" aria-hidden="true">
            <IconLock />
          </span>
          <span>
            {t('flow.consent.pointAccess', {
              partner,
              defaultValue: '{{partner}} will only have access to this verification data.',
            })}
          </span>
        </li>
        <li>
          <span className="consent-icon" aria-hidden="true">
            <IconBiometric />
          </span>
          <span>
            {t('flow.consent.pointBiometric', {
              defaultValue:
                "trustsy uses biometric technology on your images to make sure it's you. You can delete your data at any time.",
            })}
          </span>
        </li>
        {/*
          Location is disclosed HERE, in the list, not in a tooltip on the
          permission prompt. The browser prompt says "trustsy.com wants to know
          your location" and nothing else — it cannot say why, that it is
          required, or that it will be printed on a document the applicant
          signs. Collecting it without saying those things is what PDPA §19
          consent is supposed to prevent, and it is also just how a person ends
          up surprised by their own coordinates on a PDF.
        */}
        <li>
          <span className="consent-icon" aria-hidden="true">
            <IconPin />
          </span>
          <span>
            {t('flow.consent.pointLocation', {
              defaultValue:
                'Your device location is recorded when you verify and when you sign, and appears on documents you sign. Verification cannot continue without it.',
            })}
          </span>
        </li>
      </ul>

      {/*
        Two parties are being consented to here, so both get a link. The
        applicant's data is processed by trustsy AND handed to the partner; a
        screen that only ever offers trustsy's policy tells half the story.
        Partner links are omitted entirely when unset rather than rendered dead.
      */}
      <div className="consent-footer">
        <a className="consent-link" href={PRIVACY_URL} target="_blank" rel="noreferrer">
          {t('flow.consent.privacy', { defaultValue: 'trustsy Privacy Policy' })}
        </a>
        {branding?.privacy_url && (
          <a
            className="consent-link"
            href={branding.privacy_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('flow.consent.partnerPrivacy', {
              partner,
              defaultValue: '{{partner}} Privacy Policy',
            })}
          </a>
        )}
        {branding?.terms_url && (
          <a
            className="consent-link"
            href={branding.terms_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('flow.consent.partnerTerms', {
              partner,
              defaultValue: '{{partner}} Terms of Use',
            })}
          </a>
        )}
      </div>

      {denial && <LocationBlocked reason={denial} />}

      <div className="actions">
        <button
          type="button"
          className="btn"
          disabled={busy || locating}
          onClick={() => void agree()}
          data-track="accept_consent"
          data-event={CONSENT_EVENT}
        >
          {locating
            ? t('flow.consent.locating', { defaultValue: 'Checking your location…' })
            : denial && isRetryable(denial)
              ? t('flow.consent.retryLocation', { defaultValue: 'Try again' })
              : t('flow.consent.agree', { defaultValue: 'Agree and continue' })}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy || locating}
          onClick={() => setDeclined(true)}
          data-track="decline_consent"
        >
          {t('flow.consent.decline', { defaultValue: 'Decline' })}
        </button>
      </div>
    </div>
  );
}

function IconPin() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function PartnerMark() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <path
        d="M6 10c0-3 2.5-5 6-5s6 2 6 5c0 2-1 3.5-2.5 4.5L16 18H8l.5-3.5C7 13.5 6 12 6 10z"
        fill="url(#consent-partner-g)"
        opacity="0.95"
      />
      <path d="M9 11h6M10 14h4" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round" />
      <defs>
        <linearGradient id="consent-partner-g" x1="6" y1="5" x2="18" y2="18">
          <stop stopColor="#c4b5fd" />
          <stop offset="1" stopColor="#6366f1" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function IconCamera() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconBiometric() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2a5 5 0 0 1 5 5v1h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h1V7a5 5 0 0 1 5-5z" />
      <circle cx="12" cy="14" r="2" />
      <path d="M12 16v2" />
    </svg>
  );
}
